"use client"

import React, { useState, useEffect } from 'react'
import { FormField } from '../ui/form'
import { Input, InputProps } from '../ui/new-input'
import {
    FieldPath,
    FieldValues,
    Control,
    ControllerRenderProps,
    ControllerFieldState,
} from 'react-hook-form'
import FormTextInputWrapper, { InputGroupProps } from './form-input-wrapper'
import { useDirtyFields } from '../../../features/homeowners/context/dirty-fields-context'
import { useReadOnly } from '../../../features/homeowners/context/read-only-context'
import { cn } from '../../lib/utils'

/**
 * Props for the FormPhoneInput component
 * 
 * @template TFieldValues - The type of the form values object
 * @template TName - The type of the field name (must be a valid path in TFieldValues)
 */
export type FormPhoneInputProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = InputProps & {
    /** Props to pass to the InputGroup wrapper component */
    inputGroupProps?: InputGroupProps,
    /** The field name in the form (must be a valid path in TFieldValues) */
    name: TName,
    /** React Hook Form control object */
    control: Control<TFieldValues>
}

/**
 * A form phone input component that integrates with React Hook Form
 * 
 * This component renders a phone number input with smart formatting.
 * It accepts various phone number formats and handles international numbers.
 * 
 * @template TFieldValues - The type of the form values object
 * @template TName - The type of the field name (must be a valid path in TFieldValues)
 * 
 * @param props - Component props
 * @param props.inputGroupProps - Props for the InputGroup wrapper
 * @param props.name - The field name in the form
 * @param props.control - React Hook Form control object
 * @param props.inputProps - Additional input props
 * 
 * @example
 * ```tsx
 * // In your form component
 * const form = useForm<MyFormType>()
 * 
 * <FormPhoneInput
 *   name="phoneNumber"
 *   control={form.control}
 *   placeholder="(555) 123-4567 or +1 555 123 4567"
 *   inputGroupProps={{
 *     label: "Phone Number",
 *   }}
 * />
 * ```
 * 
 * @throws {Error} When the name prop is not provided
 * 
 * @returns JSX.Element - The rendered phone input component
 */
function FormPhoneInput<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues
    >>({
        inputGroupProps,
        name,
        control,
        ...inputProps
    }: FormPhoneInputProps<TFieldValues, TName>) {

    // Validate that name is provided as it's required for form integration
    if (!name) {
        throw new Error('name is required')
    }

    // Get dirty field state
    const { isFieldDirty } = useDirtyFields()
    const isDirty = isFieldDirty(name as string)
    
    // Check read-only context
    const { isReadOnly } = useReadOnly()

    return (
        <FormField
            control={control}
            name={name}
            render={({ field, fieldState }) => (
                <PhoneInput
                    field={field}
                    fieldState={fieldState}
                    isReadOnly={isReadOnly}
                    inputGroupProps={{
                        ...inputGroupProps,
                        isDirty: isDirty,
                    }}
                    {...inputProps}
                />
            )}
        />
    )
}

/**
 * Props for the PhoneInput component
 * 
 * @template TFieldValues - The type of the form values object
 * @template TName - The type of the field name (must be a valid path in TFieldValues)
 */
interface PhoneInputProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues
    >> extends InputProps {
    /** Form field render props */
    field: ControllerRenderProps<TFieldValues, TName>
    /** Form field state */
    fieldState: ControllerFieldState
    /** Props to pass to the InputGroup wrapper component */
    inputGroupProps?: InputGroupProps
    /** Whether the input is read-only */
    isReadOnly?: boolean
}

/**
 * Internal phone input component with smart formatting functionality
 * 
 * @template TFieldValues - The type of the form values object
 * @template TName - The type of the field name (must be a valid path in TFieldValues)
 * 
 * @param props - Component props
 * @param props.field - Form field render props
 * @param props.fieldState - Form field state
 * @param props.inputGroupProps - Props for the InputGroup wrapper
 * @param props.inputProps - Additional input props
 * 
 * @returns JSX.Element - The rendered phone input with smart formatting
 */
function PhoneInput<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues
    >>({ 
        field, 
        fieldState, 
        inputGroupProps,
        isReadOnly = false,
        ...inputProps 
}: PhoneInputProps<TFieldValues, TName>) {

    const [displayValue, setDisplayValue] = useState(field.value || "")
    useEffect(() => {
        setDisplayValue(field.value ?? "")
    }, [field.value])
    const errorMessage = fieldState.error?.message
    const isEmpty = field.value == null || (typeof field.value === "string" && field.value.trim() === "")
    const showEmptyHighlight = inputGroupProps?.highlightWhenEmpty && isEmpty

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (isReadOnly) return
        
        const input = e.target.value
        setDisplayValue(input); // Show what user typed
        field.onChange(input); // Send raw input to form for schema transformation
    }
    
    return (
        <FormTextInputWrapper
            inputGroupProps={inputGroupProps}
        >
                <Input
                variant={errorMessage ? "destructive" : "default"}
                value={displayValue}
                ref={field.ref}
                onBlur={field.onBlur}
                onChange={handleInputChange}
                disabled={isReadOnly || inputProps.disabled}
                placeholder={inputProps.placeholder || "(555) 123-4567 or +1 555 123 4567"}
                className={cn(
                    "transition-colors duration-200",
                    inputGroupProps?.isDirty && "border-[#9c27b0] focus:border-[#9c27b0] focus:ring-[#9c27b0]/20 shadow-sm",
                    showEmptyHighlight && "!border-[var(--color-pizza-600)] focus:!border-[var(--color-pizza-600)] focus:!ring-[var(--color-pizza-600)]/20",
                    inputProps.className
                )}
                style={showEmptyHighlight ? { backgroundColor: "var(--color-pizza-600)", opacity: 0.3, color: "#000000" } : inputProps.style}
                {...inputProps}
            />
        </FormTextInputWrapper>
    )
}

export default FormPhoneInput




